import { useQueryClient } from '@tanstack/react-query';
import Pusher, * as PusherTypes from 'pusher-js';
import { ChannelAuthorizationRequestParams } from 'pusher-js/types/src/core/auth/options';
import { useEffect, useRef, useState } from 'react';

import { useOnChannelMessage } from '../hooks/use-message-logging';
import { useMessagingDeps } from '../provider/use-messaging-deps';
import { authenticate } from '../queries/auth';
import { useMessagingConfigQuery } from '../queries/config';
import { MessagingAuthResponse, MessagingChannelType, MessagingProvider, MessagingProviderName } from '../types/messaging';
import { PusherChannelConfiguration, PusherConnectionStates } from '../types/pusher';

export type PusherChannelProps = {
  enabled: boolean;
  channelType: MessagingChannelType;
  // Allowing object as this will be necessary upon ably rollout
  // logging callabck will handle json parsing and pass along an object (rather than a string) to message handlers
  onChannelMessage: (eventName: string, data: string | object) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  registerId?: number;
};
export type UsePusherChannelHook = {
  pusherChannel: PusherTypes.Channel;
  pusherClient: Pusher;
  channelConfig: PusherChannelConfiguration;
};

// Core feature that listens for messages on the requested channel
export function usePusherChannel({
  channelType,
  enabled,
  onChannelMessage,
  onConnected,
  onDisconnected,
  registerId,
}: PusherChannelProps) {
  const queryClient = useQueryClient();
  const { pusherClient, channelConfig } = usePusherClient({
    channelType,
    enabled,
    onConnected,
    onDisconnected,
    registerId,
  });
  const [pusherChannel, setPusherChannel] = useState<PusherTypes.Channel>();
  const handleChannelMessage = useOnChannelMessage(onChannelMessage, MessagingProviderName.Pusher);

  // Ref keeps the latest handler without the effect needing it as a dep,
  // consistent with the onConnected/onDisconnected ref pattern in usePusherClient.
  const handleChannelMessageRef = useRef(handleChannelMessage);
  handleChannelMessageRef.current = handleChannelMessage;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (!pusherClient || !channelConfig) {
      return;
    }

    const channel = pusherClient.subscribe(channelConfig.ChannelName);
    const stableHandler = (eventName: string | undefined, data: string) =>
      handleChannelMessageRef.current(eventName, data);
    channel.bind_global(stableHandler);

    setPusherChannel(channel);
    return () => {
      channel.unbind();
      pusherClient.unsubscribe(channelConfig.ChannelName);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pusherClient, enabled, queryClient, channelConfig]);
  return {
    pusherChannel,
    pusherClient,
    channelConfig,
  };
}

// Custom channel auth handler — routes through the unified /api/messaging/auth/ endpoint.
// `transport` comes from the DI seam (each app's HTTP post) and is passed to authenticate().
export function useCustomChannelAuth(channelType: MessagingChannelType, registerId?: number) {
  const { transport } = useMessagingDeps();

  return async (params: ChannelAuthorizationRequestParams, callback: PusherTypes.AuthorizerCallback) => {
    try {
      const response = await authenticate<MessagingAuthResponse>(transport, {
        Provider: MessagingProvider.Pusher,
        ChannelType: channelType,
        SocketId: params.socketId,
        RegisterId: registerId,
      });

      if (response?.auth) {
        callback(null, { auth: response.auth });
      } else {
        callback(new Error('Pusher auth failed'), null);
      }
    } catch (err) {
      // Without this catch, an uncaught throw leaves the Pusher callback
      // never invoked, hanging the connection indefinitely.
      callback(err instanceof Error ? err : new Error(String(err)), null);
    }
  };
}

// Generates the pusher client
export function createPusherClient(
  channelConfig: PusherChannelConfiguration,
  channelType: MessagingChannelType,
  customChannelAuthHandler: PusherTypes.ChannelAuthorizationHandler,
) {
  return new Pusher(channelConfig?.PusherKey, {
    cluster: channelConfig?.PusherCluster,
    channelAuthorization: {
      customHandler: customChannelAuthHandler,
      transport: 'jsonp',
      // Unused while customHandler short-circuits auth in pusher-js, but kept
      // pointed at the unified path so a future SDK change can't silently fall
      // back to the legacy /pusher/auth default.
      endpoint: `/api/messaging/auth/${channelType}`,
    },
  });
}

// Creates the Pusher client with the custom channel auth handler and lifecycle hooks
export function usePusherClient({
  channelType,
  enabled,
  onConnected,
  onDisconnected,
  registerId,
}: Omit<PusherChannelProps, 'onChannelMessage'>) {
  const { data: channelConfig } = useMessagingConfigQuery(
    { Provider: MessagingProvider.Pusher, ChannelType: channelType, RegisterId: registerId },
    { enabled },
  );
  const customChannelAuthHandler = useCustomChannelAuth(channelType, registerId);
  const [pusherClient, setPusherClient] = useState<Pusher>();

  // Refs keep the latest callbacks without causing the effect to re-run (which
  // would tear down and recreate the Pusher client on every render where the
  // parent passes a new callback reference).
  const onConnectedRef = useRef(onConnected);
  const onDisconnectedRef = useRef(onDisconnected);
  onConnectedRef.current = onConnected;
  onDisconnectedRef.current = onDisconnected;

  useEffect(() => {
    if (!enabled || !channelConfig || !channelType) {
      return;
    }

    // Defined inside the effect so it captures the stable refs, not the
    // potentially-stale closure values from the render that ran the effect.
    const handleStateChange = (states: PusherConnectionStates) => {
      if (states.current === 'connected') {
        onConnectedRef.current?.();
      } else {
        onDisconnectedRef.current?.();
      }
    };

    const pusher = createPusherClient(channelConfig, channelType, customChannelAuthHandler);
    pusher.connection.bind('state_change', handleStateChange);
    setPusherClient(pusher);

    return () => {
      // Unbind before disconnect so the state_change handler doesn't fire
      // during teardown and trigger state updates on an unmounted component.
      pusher.connection.unbind('state_change', handleStateChange);
      pusher.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelConfig, channelType, enabled]);

  return {
    pusherClient,
    channelConfig,
  };
}
